import {
  AdvisorUiConfig,
  ApiInterceptorConfiguration,
  ApiInterceptorServiceConfiguration,
  PRDUIConfiguration,
} from '@blk/aw-bff-typescript-models';
import { search } from '@jmespath-community/jmespath';
import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { API_TIMEOUT, EnvType } from '../../APIConstants';
import {
  applyAGraphResponseTransformations,
  getAGraphBaseUrl,
  getApiBaseUrlSuffix,
  ResponseTransformConfig,
  setAGraphHeaders,
} from '../../utils';
import { AGraphApiSettings } from '../Base';
import { conditionalParam, isAGraphEnabled } from './AGraphUtils';
import { AW_BFF_API_URL, AW_BFF_API_URL_AGRAPH } from './Constants';

export const awBffServerSettings: AGraphApiSettings & {
  getPkgFn: () => string | undefined;
  getViewAsLoginFn?: () => string | undefined;
  getApiInterceptorConfigAndContextFn?: () => {
    apiInterceptorConfiguration: ApiInterceptorConfiguration | undefined;
    isHighEntitlementAlternativeExperience: boolean;
  };
} = {
  getPkgFn: () => undefined,
};

export const awBffApi = axios.create();

export const awBffConfigApi = axios.create();

awBffApi.interceptors.request.use((config) => interceptRequest(config));

awBffConfigApi.interceptors.request.use((config) => interceptRequest(config));

awBffConfigApi.interceptors.response.use((response: AxiosResponse<AdvisorUiConfig | PRDUIConfiguration>) => {
  const configMetadata: { [index: string]: string } = response.data?.configMetadata || {};
  Object.entries(configMetadata).forEach((entry) => {
    const [key, value] = entry;
    /* eslint-disable no-console -- Logs config paths in beta env for admin and support users */
    console.log(`${key} Config loaded from ${value}`);
  });
  return response;
});

export function interceptRequest(config: InternalAxiosRequestConfig) {
  const {
    getAladdinApiKey,
    getConfigurationDirectoryFn,
    getEnvTypeFn,
    getPkgFn,
    getViewAsLoginFn,
    getApiInterceptorConfigAndContextFn,
  } = awBffServerSettings;

  const configurationDirectory =
    config?.params?.configurationDirectory ?? (getConfigurationDirectoryFn ? getConfigurationDirectoryFn() : undefined);
  const envType = getEnvTypeFn ? getEnvTypeFn() : EnvType.DEFAULT;
  const pkg = config?.params?.pkg ?? getPkgFn();
  const apiInterceptorConfigurationAndContext = getApiInterceptorConfigAndContextFn?.();
  const interceptConfig: ApiInterceptorServiceConfiguration | undefined =
    apiInterceptorConfigurationAndContext?.apiInterceptorConfiguration?.awBff.find((ic) =>
      config.url?.includes(ic.url)
    );

  const viewAs = config?.params?.viewAs ?? (getViewAsLoginFn ? getViewAsLoginFn() : undefined);

  const baseURL = isAGraphEnabled()
    ? getAGraphBaseUrl(AW_BFF_API_URL_AGRAPH)
    : AW_BFF_API_URL + getApiBaseUrlSuffix(envType, '_');

  let finalData = config.data;
  if (interceptConfig !== undefined) {
    if (
      interceptConfig.conditionType === 'largeScaleHighEntitlement' &&
      apiInterceptorConfigurationAndContext?.isHighEntitlementAlternativeExperience
    ) {
      const { expressions } = interceptConfig.payloadTransformer;
      expressions.forEach((expression) => {
        try {
          const result = search(finalData, expression, { enable_legacy_literals: true });
          finalData = result;
        } catch (error) {
          console.warn(`API interceptor warning [${config.url}]: ${error}`);
        }
      });
    }
  }

  const updatedConfig: InternalAxiosRequestConfig = {
    ...config,
    baseURL,
    timeout: config.timeout && config.timeout !== 0 ? config.timeout : API_TIMEOUT, // use timeout from config if provided, otherwise default to API_TIMEOUT
    params: {
      ...config.params,
      pkg: conditionalParam(config.url, 'pkg', pkg),
      configurationDirectory: conditionalParam(config.url, 'configurationDirectory', configurationDirectory),
      viewAs: conditionalParam(config.url, 'viewAs', viewAs),
    },
  };
  setAGraphHeaders(updatedConfig, getAladdinApiKey?.(), envType, 'stripe', false);

  return updatedConfig;
}

// Apply response transformations for AGraph API
awBffApi.interceptors.response.use((response) => {
  if (isAGraphEnabled() && response.data) {
    const AGRAPH_RESPONSE_TRANSFORMS: ResponseTransformConfig[] = [
      {
        urlPattern: '/application-mode/evaluate-count',
        method: 'POST',
        enumPrefixesToRemove: [
          {
            fieldNames: ['applicationModes'],
            prefix: 'APPLICATION_MODE_',
            recursiveFieldPatterns: [],
          },
        ],
      },
      {
        urlPattern: '/dynamic-form-stepper/json-forms',
        method: 'GET',
        enumPrefixesToRemove: [
          {
            fieldNames: ['kind'],
            prefix: 'FIELD_KIND_',
            recursiveFieldPatterns: ['config', 'account', 'sleeve', 'steps', 'fields', 'customizations'],
          },
        ],
      },
    ];
    applyAGraphResponseTransformations(response, AGRAPH_RESPONSE_TRANSFORMS);
  }

  return response;
});
